import { createSlice } from "@reduxjs/toolkit";

import { addWebSocketCases } from "../helpers/storeAction";
import {
  addNewLocationData,
  deleteLocationData,
  fetchLocationListData,
  updateLocationListData,
} from "../thunks/locationThunks";

import type { Location } from "../../utils/types/location";

interface LocationState {
  locations: Location[];
  loading: boolean;
  error: string | null;
  isLocationsDataFetched?: boolean;
  isLocationsListDataFetched?: boolean;
}

const initialState: LocationState = {
  locations: [],
  loading: false,
  error: null,
};

export const locationSlice = createSlice({
  name: "locationList",
  initialState,
  reducers: {
    resetLocationsDataFetched: (state) => {
      state.isLocationsDataFetched = false;
    },
    resetLocationsListDataFetched: (state) => {
      state.isLocationsListDataFetched = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchLocationListData.pending, (state) => {
        state.loading = true;
        state.isLocationsDataFetched = true;
        state.error = null;
      })
      .addCase(fetchLocationListData.fulfilled, (state, action) => {
        state.locations = action.payload || [];
        state.loading = false;
      })
      .addCase(fetchLocationListData.rejected, (state, action) => {
        state.error = action.payload as string;
        state.isLocationsDataFetched = false;
        state.loading = false;
      });
    builder
      .addCase(updateLocationListData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateLocationListData.fulfilled, (state, action) => {
        state.loading = false;
        state.locations = state.locations.map((location) => {
          if (location.locationId === action.payload?.data?.locationId) {
            return { ...location, ...action.payload?.data };
          }
          return location;
        });
      })
      .addCase(updateLocationListData.rejected, (state) => {
        state.loading = false;
        state.error = "Unknown error… Please try again";
      });

    builder
      .addCase(deleteLocationData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteLocationData.fulfilled, (state, action) => {
        state.loading = false;
        state.locations = state.locations.filter(
          (location) => location.locationId !== action.payload?.id
        );
      })
      .addCase(deleteLocationData.rejected, (state) => {
        state.loading = false;
        state.error = "Unknown error… Please try again";
      });

    builder
      .addCase(addNewLocationData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addNewLocationData.fulfilled, (state, action) => {
        state.loading = false;
        state.locations = [action.payload, ...state.locations];
      })
      .addCase(addNewLocationData.rejected, (state) => {
        state.loading = false;
        state.error = "Unknown error… Please try again";
      });

    addWebSocketCases(builder, "locationList", "locations");
  },
});

export const { resetLocationsDataFetched, resetLocationsListDataFetched } =
  locationSlice.actions;
export default locationSlice.reducer;
